import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  GraduationCap, Clock, Target, Play, RotateCcw, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, AlertTriangle, Loader2, Trophy, Timer, Ban
} from 'lucide-react'
import {
  Card, CardContent, CardHeader, CardTitle,
  Button, Badge,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui'
import { LoadingSpinner } from '../components/ui/loading-states'
import { ErrorState } from '../components/ui/error-state'
import { useAuth } from '../providers/AuthProvider'
import { APP_TIME_ZONE } from '@/utils/dateFormat'
import {
  useMyTests, useStartAttempt, useMyAttempt, useSubmitAnswer, useFinishAttempt,
  type MyAssignedTest, type AttemptData, type AttemptQuestion, type FinishAttemptResponse,
} from '../hooks/testing/useTesting'

// ==================== Helpers ====================

const CATEGORY_LABELS: Record<string, string> = {
  mandatory: 'Обязательный',
  certification: 'Аттестация',
  training: 'Обучение',
}

const CATEGORY_COLORS: Record<string, string> = {
  mandatory: 'bg-red-100 text-red-800',
  certification: 'bg-blue-100 text-blue-800',
  training: 'bg-green-100 text-green-800',
}

function formatTimer(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
}

function formatTimeSpent(seconds: number): string {
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  if (m === 0) return `${s} сек`
  return `${m} мин ${s} сек`
}

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString('ru-RU', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric', month: '2-digit', day: '2-digit',
  })
}

function formatDatetime(dateString: string) {
  return new Date(dateString).toLocaleString('ru-RU', {
    timeZone: APP_TIME_ZONE,
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  })
}

// ==================== View State ====================

type PageView =
  | { mode: 'list' }
  | { mode: 'taking'; attemptId: string; testTitle: string }
  | { mode: 'results'; results: FinishAttemptResponse }

// ==================== Test Card ====================

interface TestCardProps {
  test: MyAssignedTest
  onStart: (testId: string) => void
  onResume: (attemptId: string, title: string) => void
  isStarting: boolean
}

function TestCard({ test, onStart, onResume, isStarting }: TestCardProps) {
  const { my_status: s } = test

  const renderAction = () => {
    if (s.is_passed) {
      return (
        <div className="flex items-center gap-2">
          <Badge className="bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" /> Сдан
          </Badge>
          <span className="text-sm font-medium text-green-700">{s.best_score}%</span>
        </div>
      )
    }
    if (s.in_progress_attempt_id) {
      return (
        <Button size="sm" variant="default" className="bg-yellow-600 hover:bg-yellow-700" onClick={() => onResume(s.in_progress_attempt_id!, test.title)}>
          <Play className="h-4 w-4 mr-1" /> Продолжить
        </Button>
      )
    }
    if (s.can_retry && s.attempts_used === 0) {
      return (
        <Button size="sm" onClick={() => onStart(test.test_id)} disabled={isStarting}>
          {isStarting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Play className="h-4 w-4 mr-1" />}
          Начать тест
        </Button>
      )
    }
    if (s.can_retry && s.attempts_used > 0) {
      return (
        <Button size="sm" variant="outline" onClick={() => onStart(test.test_id)} disabled={isStarting}>
          {isStarting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
          Повторить
        </Button>
      )
    }
    if (s.next_retry_at) {
      return (
        <Badge variant="outline" className="text-xs text-muted-foreground px-2 py-1">
          <Clock className="h-3 w-3 mr-1" />
          Следующая попытка в {formatDatetime(s.next_retry_at)}
        </Badge>
      )
    }
    if (s.attempts_used >= test.max_attempts) {
      return (
        <Button size="sm" variant="outline" disabled>
          <Ban className="h-4 w-4 mr-1" /> Попытки исчерпаны
        </Button>
      )
    }
    // Fallback: attempts remaining but no specific state matched — allow retry
    if (s.attempts_used > 0 && s.attempts_used < test.max_attempts) {
      return (
        <Button size="sm" variant="outline" onClick={() => onStart(test.test_id)} disabled={isStarting}>
          {isStarting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RotateCcw className="h-4 w-4 mr-1" />}
          Повторить
        </Button>
      )
    }
    return null
  }

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="font-semibold text-base truncate">{test.title}</h3>
              {test.is_required && test.category !== 'mandatory' && (
                <Badge variant="destructive" className="text-[10px] px-1.5 py-0 shrink-0">Обязательный</Badge>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap text-xs text-muted-foreground mb-3">
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 font-medium ${CATEGORY_COLORS[test.category] || ''}`}>
                {CATEGORY_LABELS[test.category] || test.category}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" /> {test.time_limit_minutes} мин
              </span>
              <span className="flex items-center gap-1">
                <Target className="h-3 w-3" /> Проходной: {test.passing_score}%
              </span>
              {test.deadline && (
                <span className="flex items-center gap-1 text-orange-600">
                  <AlertTriangle className="h-3 w-3" /> до {formatDate(test.deadline)}
                </span>
              )}
            </div>

            <div className="flex items-center gap-4 text-xs text-muted-foreground">
              <span>Попыток: {s.attempts_used} / {test.max_attempts}</span>
              {s.best_score > 0 && !s.is_passed && (
                <span>Лучший: {s.best_score}%</span>
              )}
            </div>
          </div>

          <div className="shrink-0">
            {renderAction()}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ==================== My Tests List View ====================

interface MyTestsListViewProps {
  onStart: (attemptId: string, title: string) => void
  onResume: (attemptId: string, title: string) => void
}

function MyTestsListView({ onStart, onResume }: MyTestsListViewProps) {
  const { data: tests, isLoading, error, refetch } = useMyTests()
  const startAttempt = useStartAttempt()
  const [startingTestId, setStartingTestId] = useState<string | null>(null)

  const handleStart = async (testId: string) => {
    setStartingTestId(testId)
    try {
      const result = await startAttempt.mutateAsync(testId)
      const title = tests?.find(t => t.test_id === testId)?.title ?? result.test_title ?? ''
      onStart(result.attempt_id, title)
    } catch (err: any) {
      // 409 = attempt already in progress, refetch to get the in_progress_attempt_id
      if (err?.statusCode === 409) {
        refetch()
      }
      console.error('Error starting attempt:', err)
    } finally {
      setStartingTestId(null)
    }
  }

  if (error) return <ErrorState title="Ошибка загрузки тестов" onAction={refetch} />

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!tests || tests.length === 0) {
    return (
      <div className="text-center py-16">
        <GraduationCap className="mx-auto h-12 w-12 text-muted-foreground/50" />
        <h3 className="mt-3 text-sm font-semibold text-muted-foreground">Нет назначенных тестов</h3>
        <p className="mt-1 text-sm text-muted-foreground">Когда вам назначат тест, он появится здесь.</p>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {tests.map((test) => (
        <TestCard
          key={test.test_id}
          test={test}
          onStart={handleStart}
          onResume={onResume}
          isStarting={startingTestId === test.test_id}
        />
      ))}
    </div>
  )
}

// ==================== Test Taking View ====================

interface TestTakingViewProps {
  attemptId: string
  testTitle: string
  onFinish: (results: FinishAttemptResponse) => void
  onBack: () => void
}

function TestTakingView({ attemptId, testTitle, onFinish, onBack }: TestTakingViewProps) {
  const { data: attemptData, isLoading, error } = useMyAttempt(attemptId)
  const submitAnswer = useSubmitAnswer()
  const finishAttempt = useFinishAttempt()

  const [currentIndex, setCurrentIndex] = useState(0)
  const [localSelections, setLocalSelections] = useState<Record<string, string[]>>({})
  const [timeRemaining, setTimeRemaining] = useState(0)
  const [isFinishDialogOpen, setIsFinishDialogOpen] = useState(false)
  const [isFinishing, setIsFinishing] = useState(false)
  const timerInitialized = useRef(false)
  const autoFinishTriggered = useRef(false)

  const questions: AttemptQuestion[] = attemptData?.questions || []
  const currentQuestion = questions[currentIndex]

  // Initialize local selections from server data
  useEffect(() => {
    if (attemptData?.questions) {
      const selections: Record<string, string[]> = {}
      for (const q of attemptData.questions) {
        if (q.selected_options && q.selected_options.length > 0) {
          selections[q.question_id] = q.selected_options
        }
      }
      setLocalSelections(selections)
    }
  }, [attemptData?.questions])

  // Initialize timer
  useEffect(() => {
    if (attemptData && !timerInitialized.current) {
      setTimeRemaining(Math.max(0, Math.floor(attemptData.time_remaining_seconds)))
      timerInitialized.current = true
    }
  }, [attemptData])

  // Handle timed_out response from server
  useEffect(() => {
    if (attemptData?.timed_out && attemptData.score !== undefined) {
      onFinish({
        attempt_id: attemptData.attempt_id,
        status: 'timed_out',
        score: attemptData.score,
        correct_count: attemptData.correct_count || 0,
        total_count: attemptData.total_questions,
        is_passed: attemptData.is_passed || false,
        time_spent_seconds: 0,
        finished_at: new Date().toISOString(),
      })
    }
  }, [attemptData?.timed_out])

  // Countdown timer
  useEffect(() => {
    if (timeRemaining <= 0) return

    const interval = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(interval)
          return 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [timeRemaining > 0])

  // Auto-finish when timer reaches 0
  useEffect(() => {
    if (timeRemaining === 0 && timerInitialized.current && !autoFinishTriggered.current && !isFinishing) {
      autoFinishTriggered.current = true
      handleFinish()
    }
  }, [timeRemaining])

  // Save current question answer to server
  const saveCurrentAnswer = useCallback(async () => {
    if (!currentQuestion) return
    const selected = localSelections[currentQuestion.question_id]
    if (!selected || selected.length === 0) return

    try {
      await submitAnswer.mutateAsync({
        attemptId,
        questionId: currentQuestion.question_id,
        selectedOptions: selected,
      })
    } catch (err) {
      console.error('Error saving answer:', err)
    }
  }, [currentQuestion, localSelections, attemptId])

  const navigateTo = useCallback(async (newIndex: number) => {
    if (newIndex < 0 || newIndex >= questions.length) return
    await saveCurrentAnswer()
    setCurrentIndex(newIndex)
  }, [saveCurrentAnswer, questions.length])

  const handleSelectOption = (questionId: string, optionId: string, questionType: string) => {
    setLocalSelections(prev => {
      if (questionType === 'single_choice') {
        return { ...prev, [questionId]: [optionId] }
      }
      const current = prev[questionId] || []
      if (current.includes(optionId)) {
        return { ...prev, [questionId]: current.filter(id => id !== optionId) }
      }
      return { ...prev, [questionId]: [...current, optionId] }
    })
  }

  const handleFinish = async () => {
    setIsFinishing(true)
    setIsFinishDialogOpen(false)

    // Save current answer first
    if (currentQuestion) {
      const selected = localSelections[currentQuestion.question_id]
      if (selected && selected.length > 0) {
        try {
          await submitAnswer.mutateAsync({
            attemptId,
            questionId: currentQuestion.question_id,
            selectedOptions: selected,
          })
        } catch (_) { /* continue to finish */ }
      }
    }

    try {
      const results = await finishAttempt.mutateAsync(attemptId)
      onFinish(results)
    } catch (err) {
      console.error('Error finishing attempt:', err)
      setIsFinishing(false)
    }
  }

  const answeredCount = questions.filter(q =>
    (localSelections[q.question_id] && localSelections[q.question_id].length > 0) || q.answered
  ).length

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error) {
    return <ErrorState title="Ошибка загрузки теста" onAction={onBack} actionLabel="Вернуться" />
  }

  if (!attemptData || questions.length === 0) {
    return <ErrorState title="Нет вопросов в тесте" onAction={onBack} actionLabel="Вернуться" />
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Button variant="ghost" size="sm" onClick={onBack} title="Вернуться к списку">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <h2 className="font-semibold truncate">{testTitle || attemptData?.test_title || ''}</h2>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Отвечено: {answeredCount}/{questions.length}
              </span>
              <div className={`flex items-center gap-1.5 font-mono text-lg font-bold ${timeRemaining < 60 ? 'text-red-600 animate-pulse' : timeRemaining < 300 ? 'text-orange-600' : 'text-foreground'}`}>
                <Timer className="h-5 w-5" />
                {formatTimer(timeRemaining)}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Question grid */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-1.5">
            {questions.map((q, idx) => {
              const isAnswered = (localSelections[q.question_id] && localSelections[q.question_id].length > 0) || q.answered
              const isCurrent = idx === currentIndex
              return (
                <button
                  key={q.question_id}
                  onClick={() => navigateTo(idx)}
                  className={`w-9 h-9 rounded-md text-xs font-medium transition-colors ${
                    isCurrent
                      ? 'bg-primary text-primary-foreground ring-2 ring-primary/50'
                      : isAnswered
                        ? 'bg-green-100 text-green-800 hover:bg-green-200'
                        : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  {idx + 1}
                </button>
              )
            })}
          </div>
        </CardContent>
      </Card>

      {/* Current question */}
      {currentQuestion && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline">Вопрос {currentIndex + 1} из {questions.length}</Badge>
              <Badge variant="secondary" className="text-xs">
                {currentQuestion.question_type === 'single_choice' ? 'Один ответ' : 'Несколько ответов'}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-base font-medium leading-relaxed">{currentQuestion.text}</p>

            {currentQuestion.image_url && (
              <img src={currentQuestion.image_url} alt="Вопрос" className="max-h-64 rounded-lg object-contain" />
            )}

            <div className="space-y-2">
              {currentQuestion.options.map((option) => {
                const selectedOpts = localSelections[currentQuestion.question_id] || []
                const isSelected = selectedOpts.includes(option.option_id)
                const isSingle = currentQuestion.question_type === 'single_choice'

                return (
                  <button
                    key={option.option_id}
                    onClick={() => handleSelectOption(currentQuestion.question_id, option.option_id, currentQuestion.question_type)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground/30 hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`shrink-0 w-5 h-5 ${isSingle ? 'rounded-full' : 'rounded'} border-2 flex items-center justify-center ${
                        isSelected ? 'border-primary bg-primary' : 'border-muted-foreground/40'
                      }`}>
                        {isSelected && (
                          <div className={`${isSingle ? 'w-2 h-2 rounded-full' : 'w-3 h-3'} bg-white ${!isSingle ? 'flex items-center justify-center' : ''}`}>
                            {!isSingle && (
                              <svg className="w-2.5 h-2.5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        )}
                      </div>
                      <span className="text-sm">{option.text}</span>
                    </div>
                  </button>
                )
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => navigateTo(currentIndex - 1)}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Назад
        </Button>

        {currentIndex === questions.length - 1 ? (
          <Button
            variant="destructive"
            onClick={() => setIsFinishDialogOpen(true)}
            disabled={isFinishing}
          >
            {isFinishing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
            Завершить тест
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => navigateTo(currentIndex + 1)}
          >
            Вперёд <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        )}
      </div>

      {/* Finish confirmation dialog */}
      <Dialog open={isFinishDialogOpen} onOpenChange={setIsFinishDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Завершить тест?</DialogTitle>
            <DialogDescription>
              Вы ответили на {answeredCount} из {questions.length} вопросов.
              {answeredCount < questions.length && ' Неотвеченные вопросы будут засчитаны как неправильные.'}
              {' '}Это действие нельзя отменить.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsFinishDialogOpen(false)}>Отмена</Button>
            <Button variant="destructive" onClick={handleFinish} disabled={isFinishing}>
              {isFinishing ? 'Завершение...' : 'Завершить'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ==================== Test Results View ====================

interface TestResultsViewProps {
  results: FinishAttemptResponse
  onBack: () => void
}

function TestResultsView({ results, onBack }: TestResultsViewProps) {
  const passed = results.is_passed

  return (
    <div className="flex items-center justify-center py-8">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center space-y-6">
          {/* Score circle */}
          <div className={`mx-auto w-32 h-32 rounded-full flex items-center justify-center ${
            passed ? 'bg-green-100' : 'bg-red-100'
          }`}>
            <div className="text-center">
              <div className={`text-3xl font-bold ${passed ? 'text-green-700' : 'text-red-700'}`}>
                {Math.round(results.score)}%
              </div>
            </div>
          </div>

          {/* Pass/Fail */}
          <div className="flex items-center justify-center gap-2">
            {passed ? (
              <>
                <Trophy className="h-6 w-6 text-green-600" />
                <span className="text-xl font-bold text-green-700">Тест сдан!</span>
              </>
            ) : (
              <>
                <XCircle className="h-6 w-6 text-red-600" />
                <span className="text-xl font-bold text-red-700">Тест не сдан</span>
              </>
            )}
          </div>

          {results.status === 'timed_out' && (
            <Badge variant="outline" className="text-orange-600 border-orange-300">
              <Clock className="h-3 w-3 mr-1" /> Время истекло
            </Badge>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-muted-foreground">Правильных</div>
              <div className="text-lg font-bold">{results.correct_count} / {results.total_count}</div>
            </div>
            <div className="p-3 bg-muted rounded-lg">
              <div className="text-muted-foreground">Время</div>
              <div className="text-lg font-bold">{formatTimeSpent(results.time_spent_seconds)}</div>
            </div>
          </div>

          <Button onClick={onBack} className="w-full">
            <ChevronLeft className="h-4 w-4 mr-1" /> Вернуться к тестам
          </Button>
        </CardContent>
      </Card>
    </div>
  )
}

// ==================== Main Page ====================

export function MyTests() {
  const { user } = useAuth()
  const [view, setView] = useState<PageView>({ mode: 'list' })

  if (!user?.employee?.id) {
    return (
      <div className="space-y-6 flex-1 overflow-auto">
        <div className="flex items-center gap-3">
          <GraduationCap className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Мои тесты</h1>
          </div>
        </div>
        <div className="text-center py-16">
          <AlertTriangle className="mx-auto h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-3 text-sm font-semibold text-muted-foreground">Аккаунт не привязан к сотруднику</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Обратитесь к администратору для привязки вашего аккаунта к карточке сотрудника.
          </p>
        </div>
      </div>
    )
  }

  const handleStartOrResume = (attemptId: string, title: string) => {
    setView({ mode: 'taking', attemptId, testTitle: title })
  }

  const handleResume = (attemptId: string, title: string) => {
    setView({ mode: 'taking', attemptId, testTitle: title })
  }

  const handleFinish = (results: FinishAttemptResponse) => {
    setView({ mode: 'results', results })
  }

  const handleBack = () => {
    setView({ mode: 'list' })
  }

  return (
    <div className="space-y-6 flex-1 overflow-auto">
      {view.mode === 'list' && (
        <>
          <div className="flex items-center gap-3">
            <GraduationCap className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Мои тесты</h1>
              <p className="text-muted-foreground">Назначенные тесты и аттестации</p>
            </div>
          </div>
          <MyTestsListView onStart={handleStartOrResume} onResume={handleResume} />
        </>
      )}

      {view.mode === 'taking' && (
        <TestTakingView
          attemptId={view.attemptId}
          testTitle={view.testTitle}
          onFinish={handleFinish}
          onBack={handleBack}
        />
      )}

      {view.mode === 'results' && (
        <TestResultsView results={view.results} onBack={handleBack} />
      )}
    </div>
  )
}
